import React, { useEffect, useState } from "react";
import { Chip, Tooltip } from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import i18n, {
	PSEUDO_LOCALE,
	PSEUDO_TERMS_LOCALE,
	getActivePseudoTag,
	isPseudoLocaleEnvironment,
	togglePseudoLocale,
} from "i18n";

// Floating QA control cycling OFF -> pseudo (all keys) -> pseudo (terminology
// only) -> OFF. All-keys mode brackets every translated string so hardcoded
// copy stands out; terms-only mode brackets just the Site Terminology (entity.*)
// text so QA can see exactly what a site admin's overrides drive. Renders
// nothing outside local/dev builds. While active, the chip shows a live t()
// sample as visible proof the bundle fetch -> transform -> render pipeline
// works.
// The persisted flag is the source of truth: locale resolution at boot can
// legitimately change i18n.language while pseudo stays active for captions,
// and the chip must reflect (and cycle) the flag, not the language.
const PseudoLocaleToggle = () => {
	const { t } = useTranslation();
	const [activeTag, setActiveTag] = useState(
		() =>
			getActivePseudoTag() ??
			(i18n.language === PSEUDO_LOCALE ? PSEUDO_LOCALE : null)
	);

	useEffect(() => {
		const handleLanguageChanged = (lng) =>
			setActiveTag(
				getActivePseudoTag() ?? (lng === PSEUDO_LOCALE ? PSEUDO_LOCALE : null)
			);

		i18n.on("languageChanged", handleLanguageChanged);
		return () => i18n.off("languageChanged", handleLanguageChanged);
	}, []);

	if (!isPseudoLocaleEnvironment()) {
		return null;
	}

	const label =
		activeTag === PSEUDO_TERMS_LOCALE
			? `Pseudo locale: TERMS ONLY — ${t("entity.asset")}`
			: activeTag === PSEUDO_LOCALE
				? `Pseudo locale: ON — ${t("entity.asset")}`
				: "Pseudo locale: OFF";

	return (
		<Tooltip
			// eslint-disable-next-line emi/no-hardcoded-ui-text -- dev-only tooling, never shown to end users
			title="Dev only, click to cycle: OFF -> every translated string bracketed ([!!Àššéţ!!]) to spot hardcoded copy -> only Site Terminology (entity.*) text bracketed to see what site overrides drive -> OFF. The sample after the mode is t('entity.asset') rendered live."
			placement="right"
		>
			<Chip
				icon={<TranslateIcon />}
				label={label}
				color={
					activeTag === PSEUDO_TERMS_LOCALE
						? "info"
						: activeTag === PSEUDO_LOCALE
							? "warning"
							: "default"
				}
				onClick={() => togglePseudoLocale()}
				sx={{
					position: "fixed",
					bottom: 16,
					left: 80,
					zIndex: 2000,
					boxShadow: 3,
				}}
			/>
		</Tooltip>
	);
};

export default PseudoLocaleToggle;
